// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import logger from "@insite/client-framework/Logger";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { loadPageByType } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import {
    getCurrentPage,
    getLocation,
    getPageStateByType,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { nullPage } from "@insite/client-framework/Store/Data/Pages/PagesState";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import User from "@insite/mobius/Icons/User";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import HeaderClickable, { HeaderClickableStyles } from "@west/App/Components/Header/HeaderClickable";
import MiniMyAccount from "@west/App/Components/MiniMyAccount/MiniMyAccount";
import MiniSignIn from "@west/App/Components/MiniSignIn/MiniSignIn";
import MenuTippy from "@west/App/Components/Tippy/MenuTippy";
import SidebarMenuTippy from "@west/App/Components/Tippy/SidebarMenuTippy";
import Tippy from "@west/App/Components/Tippy/Tippy";
import { CustomAppState } from "@west/App/Store/Reducers";
import { xIsAuthenticated } from "@west/App/Store/Selectors/SessionSelectors";
import Account from "@west/XDL/Icons/Account";
import { xScreen } from "@west/XDL/Utilities";
import { myAccountMapLinks } from "@west/XDL/Utilities/links";
import { myAccountFlyoutLinkClickHandler } from "@west/XDL/Utilities/tracking";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    // XNG-Changes
    myAccountLinks = "myAccountLinks",
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    showInventory: state.context.showInventory,
    showPricing: state.context.showPricing,
    isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
    hasChangeLastQty:
        state.pages.vmiBins.selectVmiBinsChangedQty &&
        Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
    // XNG-Changes
    isMyAccountPage: getCurrentPage(state).type === "MyAccountPage",
    isAuthenticated: xIsAuthenticated(state),
    myAccountLinksMapped: myAccountMapLinks(state, ownProps.fields.myAccountLinks),
    isTabletScreen: state.xCommon.isTabletScreen,
    signInPageState: getPageStateByType(state, "SignInPage"),
    xGoogleAnalytics: state.xGoogleAnalytics,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Changes
    loadPageByType,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
};

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        [fields.myAccountLinks]: LinkModel[];
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    isMyAccountPage,
    myAccountLinksMapped,
    isAuthenticated,
    isTabletScreen,
    signInPageState,
    loadPageByType,
    xGoogleAnalytics,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    const [isSidebarMenuTippyOpen, setSidebarMenuTippyOpen] = useState(false);
    const tippyInstance = React.useRef<any>(null);

    /**
     * XNG-Changes
     * Load SignInPage widgets to get fields data
     */
    useEffect(() => {
        if (!signInPageState?.value?.type || signInPageState?.value?.type === nullPage.type) {
            loadPageByType("SignInPage");
        }
    }, []);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    icon = Account;

    const onSignInHandler = (e: React.MouseEvent) => {
        if (isSignInPage) {
            return;
        }

        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
        }
    };

    const onMyAccountHandler = () => {
        if (isMyAccountPage) {
            return;
        }
        // Google Analytics click handler
        myAccountFlyoutLinkClickHandler(xGoogleAnalytics, myAccountPageLink.url, translate("My Account"));
        history.push(myAccountPageLink.url);
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const handleCreate = (instance: any) => {
        tippyInstance.current = instance;
    };

    const handleButtonClick = () => {
        if (tippyInstance.current) {
            tippyInstance.current.hide();
        }
    };

    const closeDrawer = () => {
        setSidebarMenuTippyOpen(false);
        handleButtonClick();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    const iconStyle: HeaderClickableStyles["icon"] = {
        src: showIcon ? icon : undefined,
        css: css`
            ${xScreen("lte", "md")} {
                &,
                & > svg {
                    height: 18px;
                    width: 16px;
                }
            }
        `,
    };

    const myAccountHeaderClickable = (
        <HeaderClickable
            isOpen={isSidebarMenuTippyOpen}
            variant="menu"
            onClick={onMyAccountHandler}
            extendedStyles={{
                icon: {
                    ...iconStyle,
                },
            }}
            data-test-selector="header_myAccount"
            aria-label={translate("My Account")}
        >
            {showLabel && <>{translate("My Account")}</>}
        </HeaderClickable>
    );

    if (isAuthenticated) {
        if (isTabletScreen) {
            return myAccountHeaderClickable;
        }

        return (
            <MenuTippy
                placement="bottom-end"
                onShowTrigger={isOpen => setSidebarMenuTippyOpen(isOpen)}
                maxWidth={235}
                content={
                    <MiniMyAccount
                        myAccountLinks={myAccountLinksMapped}
                        onSignOutHandler={onSignOutHandler}
                        displayChangeCustomerLink={displayChangeCustomerLink}
                    />
                }
            >
                {myAccountHeaderClickable}
            </MenuTippy>
        );
    }

    const signInHeaderClickable = (
        <HeaderClickable
            isOpen={isSidebarMenuTippyOpen}
            variant="menu"
            onClick={isTabletScreen ? onSignInHandler : undefined}
            data-test-selector="header_signIn"
            aria-label={translate("Sign In")}
            extendedStyles={{
                icon: {
                    ...iconStyle,
                },
            }}
        >
            {showLabel && <>{signInStatusText}</>}
        </HeaderClickable>
    );

    if (isTabletScreen) {
        return signInHeaderClickable;
    }

    return (
        <SidebarMenuTippy
            onCreate={handleCreate}
            content={<MiniSignIn closeSignInTippy={closeDrawer} />}
            onShowTrigger={isOpen => setSidebarMenuTippyOpen(isOpen)}
        >
            {signInHeaderClickable}
        </SidebarMenuTippy>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            // {
            //     name: fields.includeAccountMenu,
            //     displayName: "Include My Account Menu",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 3,
            // },
            {
                displayName: "My Account Links",
                name: fields.myAccountLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: item => false,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            // {
            //     name: fields.showPricingToggle,
            //     displayName: "Show/Hide Pricing Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => widget.fields.includeAccountMenu,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 5,
            // },
            // {
            //     name: fields.showInventoryToggle,
            //     displayName: "Show/Hide Inventory Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => widget.fields.includeAccountMenu,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 6,
            // },
        ],
        group: "Common",
    },
};

export default widgetModule;
